import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { useTranslation } from "react-i18next";
import { Card, cn } from "@nexus/ui";
import { MIN_SCORED_CREATIVES_FOR_INSIGHTS } from "@nexus/contracts";
import { TONE_BG, TONE_TEXT } from "../../constants/result-state";
import { useHoverIntent } from "../../hooks/useHoverIntent";
import {
  anchorForRect,
  useHoverPanelAnchor,
} from "../../hooks/useHoverPanelAnchor";
import {
  SCORE_BAND_NEEDS_REVIEW_MIN,
  SCORE_BAND_STRONG_MIN,
  bandTone,
  scoreToBand,
} from "../../lib/score-band";
import {
  averageScore,
  clusterAssetsByScore,
  rankForScore,
} from "../../lib/score-clusters";
import type { ScoredAsset } from "../../lib/score-clusters";
import type { MatrixAsset } from "../../lib/report-matrix";
import { AssetNameLabel } from "../AssetNameLabel/AssetNameLabel";
import { CollapsibleSection } from "../CollapsibleSection/CollapsibleSection";
import { HoverPanel } from "../HoverPanel/HoverPanel";
import type { HoverPanelAnchor } from "../HoverPanel/HoverPanel";
import { RailClusterDetails } from "./RailClusterDetails";

/** Max stacked dots drawn for a cluster before collapsing to a +N badge. */
const MAX_VISIBLE_STACK_DOTS = 5;

/** Score-band ranges shown in the track legend and zone segments (matches backend KTD5). */
const LEGEND_ZONES = [
  {
    tone: "error" as const,
    band: "AT_RISK" as const,
    rangeKey: "preflight.rail.zoneAtRisk" as const,
    range: {
      min: 0,
      max: SCORE_BAND_NEEDS_REVIEW_MIN - 1,
    },
    from: 0,
    to: SCORE_BAND_NEEDS_REVIEW_MIN,
  },
  {
    tone: "warning" as const,
    band: "NEEDS_REVIEW" as const,
    rangeKey: "preflight.rail.zoneNeedsReview" as const,
    range: {
      min: SCORE_BAND_NEEDS_REVIEW_MIN,
      max: SCORE_BAND_STRONG_MIN - 1,
    },
    from: SCORE_BAND_NEEDS_REVIEW_MIN,
    to: SCORE_BAND_STRONG_MIN,
  },
  {
    tone: "success" as const,
    band: "STRONG" as const,
    rangeKey: "preflight.rail.zoneStrong" as const,
    range: {
      min: SCORE_BAND_STRONG_MIN,
      max: 100,
    },
    from: SCORE_BAND_STRONG_MIN,
    to: 100,
  },
] as const;

const ZONE_BG: Record<(typeof LEGEND_ZONES)[number]["tone"], string> = {
  error: "bg-error/15",
  warning: "bg-warning/15",
  success: "bg-success/15",
};

/** Band thresholds drawn as ticks + scale labels so a dot's zone is legible at a glance. */
const THRESHOLDS = [
  SCORE_BAND_NEEDS_REVIEW_MIN,
  SCORE_BAND_STRONG_MIN,
] as const;

type BandTone = (typeof LEGEND_ZONES)[number]["tone"];
type ScoreBand = (typeof LEGEND_ZONES)[number]["band"];

/** Point on the 0–100 track a panel key points at: a cluster's score, or a zone's midpoint. */
function percentForKey(key: string): number {
  const [kind, value] = key.split(":");
  if (kind === "cluster") return Number(value);
  const zone = LEGEND_ZONES.find((candidate) => candidate.band === value);
  return zone ? (zone.from + zone.to) / 2 : 0;
}

/** Stacked dots — one disc per creative, overlapping vertically so cluster size reads at a glance. */
function ClusterStack({ count, tone }: { count: number; tone: BandTone }) {
  const visible = Math.min(count, MAX_VISIBLE_STACK_DOTS);
  const overflow = count - visible;

  return (
    <span className="relative flex flex-col items-center" aria-hidden>
      {Array.from({ length: visible }, (_, index) => (
        <span
          key={index}
          className={cn(
            "ring-background size-3.5 rounded-full shadow-sm ring-2",
            TONE_BG[tone],
            index > 0 && "-mt-2.5",
          )}
          style={{ zIndex: visible - index }}
        />
      ))}
      {overflow > 0 ? (
        <span className="bg-background text-foreground ring-border absolute -top-1.5 -right-3 z-10 flex h-4 min-w-4 items-center justify-center rounded-full px-0.5 text-[9px] leading-none font-semibold tabular-nums whitespace-nowrap ring-1">
          +{overflow}
        </span>
      ) : null}
    </span>
  );
}

/** Preview fields the rail overlays onto a dot when its owner resolves them lazily. */
export interface CreativePreview {
  thumbnailUrl: string | null;
  isVideo: boolean;
}

export interface ScoreRailProps {
  /** Every scored creative in scope — one report's creatives, or a market aggregate's. */
  assets: readonly MatrixAsset[];
  /** Jump to a creative's card when its dot is clicked. */
  onSelectAsset?: (assetId: string) => void;
  /**
   * Previews resolved outside the rail, keyed by creative. The market aggregate carries no
   * thumbnails — its owner fetches them for the panel the rail reports as open.
   */
  previewsByAssetId?: Readonly<Record<string, CreativePreview>>;
  /** Creatives a newly-opened panel shows, so their previews can be fetched on demand. */
  onPanelOpen?: (assetIds: readonly string[]) => void;
  /** Render just the panel body (no collapsible section) — used inside the insights tabs. */
  headless?: boolean;
  className?: string;
}

/**
 * Every scored creative as a dot on a single 0–100 track, coloured by band. One glance
 * shows the spread — clustered-strong vs scattered-at-risk — and outliers. Hovering or
 * focusing a dot opens a panel with the creative in context; clicking jumps to its card.
 * Renders nothing until at least two creatives can be compared.
 */
export function ScoreRail({
  assets,
  onSelectAsset,
  previewsByAssetId,
  onPanelOpen,
  headless = false,
  className,
}: ScoreRailProps) {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState<string | null>(null);
  const hover = useHoverIntent<string>();
  const { anchor, pinTo } = useHoverPanelAnchor(hover.openKey !== null);
  const railRef = useRef<HTMLDivElement | null>(null);
  const dotRefs = useRef(new Map<string, HTMLButtonElement | null>());
  /** Touch has no hover, so the first tap on a cluster opens its panel instead of jumping. */
  const isCoarsePointer = useRef(false);
  const panelIdPrefix = useId();

  const dots = useMemo(() => {
    const scored = [...assets]
      .filter((a): a is ScoredAsset => a.score != null)
      .sort((a, b) => a.score - b.score);
    if (!previewsByAssetId) return scored;
    return scored.map((dot) => {
      const preview = previewsByAssetId[dot.id];
      return preview ? { ...dot, ...preview } : dot;
    });
  }, [assets, previewsByAssetId]);

  const clusters = useMemo(() => clusterAssetsByScore(dots), [dots]);
  const average = useMemo(() => averageScore(dots), [dots]);

  const zoneCounts = useMemo(() => {
    const counts: Record<ScoreBand, number> = {
      AT_RISK: 0,
      NEEDS_REVIEW: 0,
      STRONG: 0,
    };
    for (const dot of dots) {
      const band = dot.band ?? scoreToBand(dot.score);
      counts[band] += 1;
    }
    return counts;
  }, [dots]);

  /** Points a panel at a score on the track rather than at the middle of the rail. */
  const pinToScore = useCallback(
    (key: string) =>
      pinTo(() =>
        anchorForRect(
          railRef.current?.getBoundingClientRect(),
          percentForKey(key),
        ),
      ),
    [pinTo],
  );

  const openKey = hover.openKey;

  // Touch never fires a pointer-leave, so a tap outside the rail is what dismisses a panel.
  useEffect(() => {
    if (openKey === null) return;
    const handleOutsidePointerDown = (event: PointerEvent) => {
      if (railRef.current?.contains(event.target as Node | null)) return;
      hover.closeNow();
      setActiveId(null);
    };
    document.addEventListener("pointerdown", handleOutsidePointerDown);
    return () =>
      document.removeEventListener("pointerdown", handleOutsidePointerDown);
  }, [openKey, hover.closeNow]);

  if (dots.length < MIN_SCORED_CREATIVES_FOR_INSIGHTS) return null;

  const active = dots.find((d) => d.id === activeId) ?? null;
  const activeBand = active ? (active.band ?? scoreToBand(active.score)) : null;

  const openZone =
    LEGEND_ZONES.find((zone) => hover.openKey === `zone:${zone.band}`) ?? null;
  const openZoneShare = openZone
    ? Math.round((zoneCounts[openZone.band] / dots.length) * 100)
    : 0;

  const openAfterDwell = (key: string, panelAssetIds?: readonly string[]) => {
    pinToScore(key);
    if (panelAssetIds) onPanelOpen?.(panelAssetIds);
    hover.open(key);
  };

  const openImmediately = (key: string, panelAssetIds?: readonly string[]) => {
    pinToScore(key);
    if (panelAssetIds) onPanelOpen?.(panelAssetIds);
    hover.openNow(key);
  };

  const closeAndClear = () => {
    hover.close();
    setActiveId(null);
  };

  const body = (
    <>
      <p className="text-muted-foreground mb-4 text-sm">
        {t("preflight.rail.description", { count: dots.length })}
      </p>

      <div
        ref={railRef}
        className="relative py-4"
        onPointerLeave={closeAndClear}
        onBlur={(event) => {
          if (railRef.current?.contains(event.relatedTarget as Node | null))
            return;
          hover.closeNow();
          setActiveId(null);
        }}
      >
        <div className="bg-muted ring-border/50 motion-safe:animate-in motion-safe:fade-in relative h-2.5 w-full overflow-hidden rounded-full shadow-inner ring-1 ring-inset motion-safe:duration-500 motion-safe:ease-out">
          {LEGEND_ZONES.map((zone) => {
            const bandLabel = t(`preflight.band.${zone.band}`);
            const rangeLabel = t(zone.rangeKey, zone.range);
            const count = zoneCounts[zone.band];
            const zoneKey = `zone:${zone.band}`;
            return (
              <button
                key={zone.band}
                type="button"
                tabIndex={-1}
                aria-label={t("preflight.rail.zoneSegmentLabel", {
                  band: bandLabel,
                  range: rangeLabel,
                  count,
                })}
                onPointerEnter={(event) => {
                  if (event.pointerType !== "mouse") return;
                  openAfterDwell(zoneKey);
                }}
                onPointerLeave={hover.close}
                className={cn(
                  "focus-visible:ring-ring/60 absolute inset-y-0 z-0 cursor-default border-0 p-0 transition-colors hover:brightness-95 focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none",
                  ZONE_BG[zone.tone],
                )}
                style={{
                  left: `${zone.from}%`,
                  width: `${zone.to - zone.from}%`,
                }}
              />
            );
          })}
          {THRESHOLDS.map((threshold) => (
            <span
              key={threshold}
              aria-hidden
              className="bg-background/70 absolute inset-y-0 w-px"
              style={{ left: `${threshold}%` }}
            />
          ))}
        </div>

        {openZone ? (
          <HoverPanel
            id={`${panelIdPrefix}-zone:${openZone.band}`}
            anchor={anchor}
          >
            <div className="flex flex-col gap-1.5 px-3 py-2.5">
              <span className="text-foreground flex items-center gap-1.5 text-xs font-medium">
                <span
                  className={cn(
                    "size-1.5 rounded-full",
                    TONE_BG[openZone.tone],
                  )}
                  aria-hidden
                />
                {t(`preflight.band.${openZone.band}`)}
              </span>
              <span className="text-muted-foreground text-[11px] tabular-nums">
                {t(openZone.rangeKey, openZone.range)}
              </span>
              <span
                className="bg-muted h-1 w-full overflow-hidden rounded-full"
                aria-hidden
              >
                <span
                  className={cn(
                    "block h-full rounded-full",
                    TONE_BG[openZone.tone],
                  )}
                  style={{ width: `${openZoneShare}%` }}
                />
              </span>
              <span className="text-muted-foreground text-[11px] tabular-nums">
                {t("preflight.rail.hoverZoneShare", {
                  count: zoneCounts[openZone.band],
                  total: dots.length,
                  percent: openZoneShare,
                })}
              </span>
            </div>
          </HoverPanel>
        ) : null}

        {clusters.map((cluster, index) => {
          const lead = cluster.assets[0];
          const tone = bandTone(lead.band ?? scoreToBand(cluster.score));
          const isClusterActive = cluster.assets.some((a) => a.id === activeId);
          const isMulti = cluster.assets.length > 1;
          const clusterKey = `cluster:${cluster.score}`;
          const isOpen = hover.openKey === clusterKey;
          const dotLabel = isMulti
            ? t("preflight.rail.clusterDotLabel", {
                count: cluster.assets.length,
                score: cluster.score,
              })
            : t("preflight.rail.dotLabel", {
                asset: lead.label,
                score: cluster.score,
              });

          const panelAssetIds = cluster.assets.map((a) => a.id);

          const handleClick = () => {
            // A tap can't hover first, so a cluster needs one tap to reveal its creatives.
            if (isCoarsePointer.current && isMulti && !isOpen) {
              setActiveId(lead.id);
              openImmediately(clusterKey, panelAssetIds);
              return;
            }
            const target =
              cluster.assets.find((a) => a.id === activeId) ?? lead;
            // The panel is anchored to the dot, so it has to go before the page scrolls away.
            hover.closeNow();
            onSelectAsset?.(target.id);
          };

          const trackPointerType = (event: ReactPointerEvent) => {
            isCoarsePointer.current = event.pointerType !== "mouse";
          };

          return (
            <Fragment key={cluster.score}>
              <button
                type="button"
                ref={(node) => {
                  dotRefs.current.set(clusterKey, node);
                }}
                aria-label={dotLabel}
                aria-haspopup="dialog"
                aria-expanded={isOpen}
                aria-controls={
                  isOpen ? `${panelIdPrefix}-${clusterKey}` : undefined
                }
                onPointerDown={trackPointerType}
                onPointerEnter={(event) => {
                  trackPointerType(event);
                  setActiveId(lead.id);
                  if (event.pointerType !== "mouse") return;
                  openAfterDwell(clusterKey, panelAssetIds);
                }}
                onPointerLeave={hover.close}
                onFocus={() => {
                  setActiveId(lead.id);
                  openImmediately(clusterKey, panelAssetIds);
                }}
                onClick={handleClick}
                className={cn(
                  "ring-background focus-visible:ring-ring/60 motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-0 motion-safe:fill-mode-both absolute top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center rounded-full outline-none transition-transform duration-200 ease-out hover:scale-150 focus-visible:ring-[3px] motion-reduce:transition-none",
                  onSelectAsset != null ? "cursor-pointer" : "cursor-default",
                  isOpen || isClusterActive ? "z-10 scale-150" : "z-[1]",
                  !isMulti &&
                    cn(
                      "size-3.5 shadow-sm ring-2",
                      TONE_BG[tone],
                      isClusterActive && "shadow-md",
                    ),
                )}
                style={{
                  left: `${cluster.score}%`,
                  animationDelay: `${Math.min(index, 16) * 60}ms`,
                  animationDuration: "420ms",
                }}
              >
                {isMulti ? (
                  <ClusterStack count={cluster.assets.length} tone={tone} />
                ) : null}
              </button>
              {isOpen ? (
                <HoverPanel
                  id={`${panelIdPrefix}-${clusterKey}`}
                  anchor={anchor}
                  label={dotLabel}
                  isInteractive
                  onPointerEnter={hover.keepOpen}
                  onPointerLeave={hover.close}
                  onEscape={() => {
                    hover.closeNow();
                    dotRefs.current.get(clusterKey)?.focus();
                  }}
                >
                  <RailClusterDetails
                    score={cluster.score}
                    assets={cluster.assets}
                    rank={rankForScore(dots, cluster.score)}
                    total={dots.length}
                    deltaFromAverage={cluster.score - average}
                    activeId={activeId}
                    isSelectable={onSelectAsset != null}
                    onActivateAsset={setActiveId}
                    onSelectAsset={(assetId) => {
                      hover.closeNow();
                      onSelectAsset?.(assetId);
                    }}
                  />
                </HoverPanel>
              ) : null}
            </Fragment>
          );
        })}
      </div>

      <div className="text-muted-foreground relative mt-1 h-4 text-[11px] tabular-nums">
        <span className="absolute left-0">0</span>
        {THRESHOLDS.map((threshold) => (
          <span
            key={threshold}
            className="absolute -translate-x-1/2"
            style={{ left: `${threshold}%` }}
          >
            {threshold}
          </span>
        ))}
        <span className="absolute right-0">100</span>
      </div>

      <ul
        className="text-muted-foreground mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px]"
        aria-label={t("preflight.rail.legendTitle")}
      >
        {LEGEND_ZONES.map((zone) => (
          <li key={zone.band} className="inline-flex items-center gap-1.5">
            <span
              className={cn("size-2 shrink-0 rounded-full", TONE_BG[zone.tone])}
              aria-hidden
            />
            <span>
              {t("preflight.rail.zoneLegend", {
                band: t(`preflight.band.${zone.band}`),
                range: t(zone.rangeKey, zone.range),
              })}
            </span>
          </li>
        ))}
      </ul>

      <div className="border-border/60 mt-3 flex min-h-5 items-center border-t pt-3 text-xs">
        {active && activeBand ? (
          <span className="flex w-full min-w-0 items-center gap-1.5 whitespace-nowrap">
            <span
              className={cn(
                "size-1.5 shrink-0 rounded-full",
                TONE_BG[bandTone(activeBand)],
              )}
              aria-hidden
            />
            <AssetNameLabel
              name={active.label}
              className="text-foreground min-w-0 shrink font-medium"
            />
            <span
              className={cn(
                "shrink-0 tabular-nums",
                TONE_TEXT[bandTone(activeBand)],
              )}
            >
              · {Math.round(active.score)} / 100
            </span>
            <span className="text-muted-foreground shrink-0">
              · {t(`preflight.band.${activeBand}`)}
            </span>
          </span>
        ) : (
          <span className="text-muted-foreground">
            {t("preflight.rail.hint")}
          </span>
        )}
      </div>
    </>
  );

  if (headless) return body;

  return (
    <CollapsibleSection title={t("preflight.rail.title")} className={className}>
      <Card className="border-border/60 gap-0 p-5">{body}</Card>
    </CollapsibleSection>
  );
}
